import { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'

const GRADE_LEVELS = ['Junior 2', 'Junior 3', 'Junior 4', 'Junior 5', 'Junior 6','Middle 1', 'Middle 2', 'Middle 3', 'Senior 1', 'Senior 2']

export default function Attendance() {
  const [groups, setGroups] = useState([])
  const [selectedGradeLevel, setSelectedGradeLevel] = useState(null)
  const [selectedGroup, setSelectedGroup] = useState(null)
  const [students, setStudents] = useState([])
  const [attendance, setAttendance] = useState({})
  const [date, setDate] = useState(new Date().toISOString().split('T')[0])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  useEffect(() => { fetchGroups() }, [])
  useEffect(() => { if (selectedGroup) fetchStudents() }, [selectedGroup])
  useEffect(() => { if (students.length) fetchAttendance() }, [date, students])

  async function fetchGroups() {
    const { data } = await supabase
      .from('groups')
      .select('*')
      .order('grade_level')
      .order('name')

    setGroups(data || [])
    setLoading(false)
  }

  async function fetchStudents() {
    const { data } = await supabase
      .from('students')
      .select('*')
      .eq('group_id', selectedGroup.id)
      .order('name')

    setStudents(data || [])

    const init = {}
    data?.forEach(s => {
      init[s.id] = 'present'
    })

    setAttendance(init)
  }

  async function fetchAttendance() {
    if (!students.length) return

    const { data } = await supabase
      .from('attendance')
      .select('*')
      .in('student_id', students.map(s => s.id))
      .eq('date', date)

    if (data?.length) {
      const map = {}
      data.forEach(a => {
        map[a.student_id] = a.status
      })
      setAttendance(map)
    }
  }

  async function save() {
    setSaving(true)

    const records = students.map(s => ({
      student_id: s.id,
      date,
      status: attendance[s.id] || 'present'
    }))

    const { error } = await supabase
      .from('attendance')
      .upsert(records, { onConflict: 'student_id,date' })

    if (error) {
      alert(error.message)
    }

    setSaving(false)
    setSaved(true)

    setTimeout(() => setSaved(false), 2000)
  }

  function toggle(id) {
    const order = ['present', 'absent', 'late']
    const cur = attendance[id] || 'present'

    setAttendance(p => ({
      ...p,
      [id]: order[(order.indexOf(cur) + 1) % 3]
    }))
  }

  const statusCfg = {
    present: {
      label:'Present',
      color:'var(--success)',
      bg:'var(--success-bg)'
    },
    absent: {
      label:'Absent',
      color:'var(--danger)',
      bg:'var(--danger-bg)'
    },
    late: {
      label:'Late',
      color:'var(--warning)',
      bg:'var(--warning-bg)'
    },
  }

  const filteredGroups = selectedGradeLevel
    ? groups.filter(g => g.grade_level === selectedGradeLevel)
    : groups

  const present = Object.values(attendance)
    .filter(s => s === 'present').length

  const absent = Object.values(attendance)
    .filter(s => s === 'absent').length

  const late = Object.values(attendance)
    .filter(s => s === 'late').length

  if (loading) return <div className="empty">Loading...</div>

  return (
    <div>

      <div
        style={{
          display:'flex',
          justifyContent:'space-between',
          alignItems:'center',
          marginBottom:20
        }}
      >
        <h2 style={{ fontSize:20, fontWeight:500 }}>
          Attendance
        </h2>

        {selectedGroup && (
          <button
            className="btn btn-primary"
            onClick={save}
            disabled={saving}
          >
            {saving
              ? 'Saving...'
              : saved
                ? '✓ Saved'
                : 'Save Attendance'}
          </button>
        )}
      </div>

      {/* Step 1: Grade Level */}
      <div
        className="card"
        style={{ marginBottom:16 }}
      >
        <div
          style={{
            fontSize:13,
            color:'var(--text3)',
            marginBottom:10
          }}
        >
          Step 1 — Select Grade Level
        </div>

        <div
          style={{
            display:'flex',
            gap:8,
            flexWrap:'wrap'
          }}
        >

          <button
            className={`chip ${!selectedGradeLevel ? 'active' : ''}`}
            onClick={() => {
              setSelectedGradeLevel(null)
              setSelectedGroup(null)
              setStudents([])
            }}
          >
            All
          </button>

          {GRADE_LEVELS.map(l => (
            <button
              key={l}
              className={`chip ${
                selectedGradeLevel === l ? 'active' : ''
              }`}
              onClick={() => {
                setSelectedGradeLevel(l)
                setSelectedGroup(null)
                setStudents([])
              }}
            >
              {l}
            </button>
          ))}

        </div>
      </div>

      {/* Step 2: Group */}
      {filteredGroups.length > 0 && (
        <div
          className="card"
          style={{ marginBottom:16 }}
        >

          <div
            style={{
              fontSize:13,
              color:'var(--text3)',
              marginBottom:10
            }}
          >
            Step 2 — Select Group
          </div>

          <div
            style={{
              display:'flex',
              gap:8,
              flexWrap:'wrap'
            }}
          >

            {filteredGroups.map(g => (
              <button
                key={g.id}
                className={`chip ${
                  selectedGroup?.id === g.id ? 'active' : ''
                }`}
                onClick={() => setSelectedGroup(g)}
              >
                {g.name}

                {g.day && (
                  <span
                    style={{
                      fontSize:11,
                      marginLeft:4,
                      opacity:.7
                    }}
                  >
                    · {g.day}
                  </span>
                )}

                {g.time && (
                  <span
                    style={{
                      fontSize:11,
                      marginLeft:2,
                      opacity:.7
                    }}
                  >
                    {g.time}
                  </span>
                )}
              </button>
            ))}

          </div>
        </div>
      )}

      {/* Step 3: Date + Students */}
      {selectedGroup && (
        <>

          <div
            className="card"
            style={{
              marginBottom:16,
              display:'flex',
              gap:16,
              flexWrap:'wrap',
              alignItems:'center'
            }}
          >

            <div>

              <label
                style={{
                  fontSize:12,
                  color:'var(--text3)',
                  display:'block',
                  marginBottom:4
                }}
              >
                Step 3 — Date
              </label>

              <input
                type="date"
                value={date}
                onChange={e => setDate(e.target.value)}
                style={{ width:'auto' }}
              />

            </div>

            <div
              style={{
                display:'flex',
                gap:10,
                marginLeft:'auto'
              }}
            >

              <span className="badge badge-success">
                ✓ {present}
              </span>

              <span className="badge badge-danger">
                ✗ {absent}
              </span>

              <span className="badge badge-warning">
                ⏱ {late}
              </span>

              <span
                style={{
                  fontSize:13,
                  color:'var(--text3)'
                }}
              >
                / {students.length}
              </span>

            </div>

          </div>

          {students.length === 0 ? (
            <div className="empty">
              No students in this group
            </div>
          ) : (
            <>

              {/* Desktop */}
              <div className="desktop-table">

                <div
                  className="card"
                  style={{
                    padding:0,
                    overflow:'hidden'
                  }}
                >

                  <table>

                    <thead>
                      <tr>
                        <th>Student</th>
                        <th>Grade</th>
                        <th>Status</th>
                      </tr>
                    </thead>

                    <tbody>

                      {students.map(s => {

                        const st = attendance[s.id] || 'present'
                        const cfg = statusCfg[st]

                        return (
                          <tr key={s.id}>

                            <td>

                              <div
                                style={{
                                  display:'flex',
                                  alignItems:'center',
                                  gap:10
                                }}
                              >

                                <div
                                  style={{
                                    width:32,
                                    height:32,
                                    borderRadius:'50%',
                                    background:'var(--accent-bg)',
                                    color:'var(--accent)',
                                    display:'flex',
                                    alignItems:'center',
                                    justifyContent:'center',
                                    fontWeight:500
                                  }}
                                >
                                  {s.name.charAt(0).toUpperCase()}
                                </div>

                                {s.name}

                              </div>

                            </td>

                            <td>
                              {s.grade && (
                                <span className="badge badge-accent">
                                  {s.grade}
                                </span>
                              )}
                            </td>

                            <td>

                              <button
                                onClick={() => toggle(s.id)}
                                style={{
                                  padding:'5px 16px',
                                  borderRadius:20,
                                  border:'none',
                                  cursor:'pointer',
                                  fontWeight:500,
                                  fontSize:13,
                                  background:cfg.bg,
                                  color:cfg.color
                                }}
                              >
                                {cfg.label}
                              </button>

                            </td>

                          </tr>
                        )

                      })}

                    </tbody>

                  </table>

                </div>

              </div>

              {/* Mobile */}
              <div className="mobile-cards">

                {students.map(s => {

                  const st = attendance[s.id] || 'present'
                  const cfg = statusCfg[st]

                  return (
                    <div
                      key={s.id}
                      className="mobile-card"
                      style={{
                        display:'flex',
                        alignItems:'center',
                        gap:12
                      }}
                    >

                      <div
                        style={{
                          width:40,
                          height:40,
                          borderRadius:'50%',
                          background:'var(--accent-bg)',
                          color:'var(--accent)',
                          display:'flex',
                          alignItems:'center',
                          justifyContent:'center',
                          fontWeight:600,
                          fontSize:17,
                          flexShrink:0
                        }}
                      >
                        {s.name.charAt(0).toUpperCase()}
                      </div>

                      <div style={{ flex:1 }}>

                        <div
                          style={{
                            fontWeight:600,
                            fontSize:15
                          }}
                        >
                          {s.name}
                        </div>

                        {s.grade && (
                          <div
                            style={{
                              fontSize:12,
                              color:'var(--text3)',
                              marginTop:2
                            }}
                          >
                            {s.grade}
                          </div>
                        )}

                      </div>

                      <button
                        onClick={() => toggle(s.id)}
                        style={{
                          padding:'7px 18px',
                          borderRadius:20,
                          border:'none',
                          cursor:'pointer',
                          fontWeight:600,
                          fontSize:13,
                          background:cfg.bg,
                          color:cfg.color,
                          flexShrink:0
                        }}
                      >
                        {cfg.label}
                      </button>

                    </div>
                  )

                })}

              </div>

            </>
          )}

        </>
      )}

      {!selectedGroup && filteredGroups.length === 0 && groups.length > 0 && (
        <div className="empty">
          No groups in this grade level
        </div>
      )}

      {groups.length === 0 && (
        <div className="empty">
          No groups yet — add groups from the Students page
        </div>
      )}

    </div>
  )
}